import { useContext, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FileImageOutlined, TeamOutlined, HistoryOutlined } from '@ant-design/icons';
import { useAbility } from '@casl/react';
import { AbilityContext } from 'app/atoms';
import { PermissionActions, PermissionSubjects } from 'configs/permissionConstants';
import { NavBreadcrumbContext } from 'app/molecules/NavBreadcrumb/NavBreadcrumbContext';
import { getContentItems } from 'app/modules/ContentItems/service';
import { ContentItemStatus } from 'app/modules/ContentItems/types';
import { getUsersList } from 'app/modules/UserManagement/service';
import { UserResponse } from 'app/modules/Auth/types';
import { AUDIT_LOGS } from 'configs/routeNames';
import { useNavigate } from 'react-router-dom';

const LandingPage = () => {
  const { setItems } = useContext(NavBreadcrumbContext);
  const ability = useAbility(AbilityContext);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const userInfo = queryClient.getQueryData<UserResponse>(['auth', 'user-info']);
  const canManageUsers = ability.can(PermissionActions.MANAGE, PermissionSubjects.USER_MANAGEMENT);
  const canViewActivityLogs = ability.can(PermissionActions.VIEW, PermissionSubjects.ACTIVITY_LOGS);

  useEffect(() => {
    setItems([{ title: 'Dashboard' }]);
  }, [setItems]);

  const { data: items = [] } = useQuery({ queryKey: ['content-items'], queryFn: getContentItems });
  const { data: users = [] } = useQuery({ queryKey: ['users-list'], queryFn: getUsersList, enabled: canManageUsers });

  const publishedCount = items.filter((item) => item.status === ContentItemStatus.PUBLISHED).length;

  return (
    <div className="mx-auto max-w-5xl">
      <h2 className="text-xl font-semibold font-figtree mb-1">
        Welcome{userInfo?.displayName ? `, ${userInfo.displayName}` : ''}
      </h2>
      <p className="text-gray-500 font-figtree mb-6">Here&apos;s a quick look at the Urban Mobility CMS.</p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg border p-6 flex items-center gap-4">
          <FileImageOutlined className="text-3xl text-[#000032]" />
          <div>
            <p className="text-2xl font-semibold font-figtree">{items.length}</p>
            <p className="text-gray-500 text-sm font-figtree">Onboarding screens ({publishedCount} published)</p>
          </div>
        </div>
        {canManageUsers && (
          <div className="bg-white rounded-lg border p-6 flex items-center gap-4">
            <TeamOutlined className="text-3xl text-[#000032]" />
            <div>
              <p className="text-2xl font-semibold font-figtree">{users.length}</p>
              <p className="text-gray-500 text-sm font-figtree">Invited users</p>
            </div>
          </div>
        )}
        {canViewActivityLogs && (
          <button
            type="button"
            onClick={() => navigate(`/${AUDIT_LOGS}`)}
            className="text-left bg-white rounded-lg border p-6 flex items-center gap-4 hover:shadow-sm transition-shadow"
          >
            <HistoryOutlined className="text-3xl text-[#000032]" />
            <p className="text-gray-500 text-sm font-figtree">Review sign-ins, invites and publishing activity in Activity Logs</p>
          </button>
        )}
      </div>
    </div>
  );
};

export default LandingPage;
